import { useState } from 'react';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  Label,
  cn,
} from '@pantry/ui';
import {
  useDeleteStorageLocation,
  useMoveStorageLocationItems,
  useStorageLocations,
} from '@pantry/api-client';
import {
  ExclamationTriangleIcon,
  TrashIcon,
  ArrowRightIcon,
  CheckCircleIcon,
  ArrowLeftIcon,
  ArchiveBoxIcon,
} from '@heroicons/react/24/outline';

interface DeleteStorageLocationDialogProps {
  householdId: string;
  locationId: string;
  locationName: string;
  itemCount?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
}

type Step = 'confirm' | 'selectDestination' | 'moving' | 'deleting';

/**
 * Multi-step dialog for safe storage location deletion.
 * If location has items, requires moving them to another location first.
 */
export function DeleteStorageLocationDialog({
  householdId,
  locationId,
  locationName,
  itemCount = 0,
  open,
  onOpenChange,
  onSuccess,
}: DeleteStorageLocationDialogProps) {
  const { t } = useLingui();

  const getStorageTypeLabel = (type: string): string => {
    switch (type) {
      case 'fridge':
        return t`Fridge`;
      case 'freezer':
        return t`Freezer`;
      case 'pantry':
        return t`Pantry`;
      case 'other':
        return t`Other`;
      default:
        return type;
    }
  };

  const [step, setStep] = useState<Step>('confirm');
  const [error, setError] = useState<string | null>(null);
  const [selectedDestination, setSelectedDestination] = useState<string | null>(null);
  const [movedCount, setMovedCount] = useState(0);
  const [localItemCount, setLocalItemCount] = useState(itemCount);

  const deleteLocation = useDeleteStorageLocation();
  const moveItems = useMoveStorageLocationItems();
  const { data: allLocations } = useStorageLocations(householdId);

  // Get other locations for destination picker
  const otherLocations = allLocations?.filter((loc) => loc.id !== locationId) ?? [];

  const hasItems = localItemCount > 0;

  const handleMoveItems = async () => {
    if (!selectedDestination) return;

    setStep('moving');
    setError(null);

    try {
      const result = await moveItems.mutateAsync({
        householdId,
        sourceId: locationId,
        destinationId: selectedDestination,
      });
      setMovedCount(result.movedCount);
      setLocalItemCount(0);
      // After successful move, go back to delete confirmation
      setStep('confirm');
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to move items`;
      setError(message);
      setStep('selectDestination');
    }
  };

  const handleDelete = async () => {
    setStep('deleting');
    setError(null);

    try {
      await deleteLocation.mutateAsync({ householdId, id: locationId });
      handleClose();
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : t`Failed to delete storage location`;
      setError(message);
      setStep('confirm');
    }
  };

  const handleClose = () => {
    setStep('confirm');
    setError(null);
    setSelectedDestination(null);
    setMovedCount(0);
    setLocalItemCount(itemCount);
    onOpenChange(false);
  };

  const renderContent = () => {
    switch (step) {
      case 'confirm':
        return (
          <>
            <DialogHeader>
              <DialogTitle className="font-display flex items-center gap-3 text-destructive">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-destructive/10">
                  <ExclamationTriangleIcon className="h-5 w-5 text-destructive" />
                </div>
                <Trans>Delete Location</Trans>
              </DialogTitle>
              <DialogDescription className="pt-2">
                <Trans>
                  Are you sure you want to delete{' '}
                  <span className="font-semibold text-foreground">{locationName}</span>?
                </Trans>
              </DialogDescription>
            </DialogHeader>

            {/* Warning for items - requires moving first */}
            {hasItems && (
              <div className="rounded-xl bg-muted/80 border border-border p-4">
                <div className="flex items-start gap-3">
                  <ArchiveBoxIcon className="h-5 w-5 text-foreground mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-foreground">
                      <Trans>
                        This location has{' '}
                        <Plural value={localItemCount} one="# item" other="# items" />
                      </Trans>
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      <Trans>You must move all items to another location before deleting.</Trans>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Success message if items were moved */}
            {movedCount > 0 && !hasItems && (
              <div className="rounded-xl bg-green-50 dark:bg-green-950/30 border border-green-200 dark:border-green-800/50 p-4 animate-in fade-in slide-in-from-top-2">
                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
                  <p className="text-sm text-green-800 dark:text-green-200">
                    <Trans>
                      <Plural value={movedCount} one="# item" other="# items" /> moved successfully.
                      You can now delete this location.
                    </Trans>
                  </p>
                </div>
              </div>
            )}

            {error && (
              <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2">
                {error}
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={deleteLocation.isPending}
                className="rounded-xl"
              >
                <Trans>Cancel</Trans>
              </Button>
              {hasItems ? (
                <Button onClick={() => setStep('selectDestination')} className="rounded-xl">
                  <ArrowRightIcon className="h-4 w-4 mr-2" />
                  <Trans>Move Items</Trans>
                </Button>
              ) : (
                <Button
                  onClick={handleDelete}
                  disabled={deleteLocation.isPending}
                  className="rounded-xl bg-destructive hover:bg-destructive/90 text-white"
                >
                  <TrashIcon className="h-4 w-4 mr-2" />
                  <Trans>Delete Location</Trans>
                </Button>
              )}
            </DialogFooter>
          </>
        );

      case 'selectDestination':
        return (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-2">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setStep('confirm')}
                  className="rounded-xl h-8 w-8 -ml-1"
                >
                  <ArrowLeftIcon className="h-4 w-4" />
                </Button>
                <DialogTitle className="font-display text-foreground">
                  <Trans>Move Items</Trans>
                </DialogTitle>
              </div>
              <DialogDescription>
                <Trans>
                  Moving <Plural value={localItemCount} one="# item" other="# items" /> from{' '}
                  {locationName}
                </Trans>
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-3">
              <Label className="text-muted-foreground">
                <Trans>Select destination location:</Trans>
              </Label>

              {otherLocations.length === 0 ? (
                <div className="rounded-xl bg-muted p-6 text-center">
                  <ArchiveBoxIcon className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                  <p className="text-sm text-muted-foreground">
                    <Trans>No other locations available.</Trans>
                    <br />
                    <Trans>Create another location first.</Trans>
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[240px] overflow-y-auto">
                  {otherLocations.map((loc) => {
                    const isSelected = selectedDestination === loc.id;
                    return (
                      <button
                        key={loc.id}
                        onClick={() => setSelectedDestination(loc.id)}
                        className={cn(
                          'w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left',
                          isSelected
                            ? 'border-primary bg-primary/5'
                            : 'border-border hover:border-primary/30 hover:bg-muted/50'
                        )}
                      >
                        <div
                          className={cn(
                            'flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl',
                            isSelected ? 'bg-primary/10' : 'bg-muted'
                          )}
                        >
                          <ArchiveBoxIcon
                            className={cn(
                              'h-5 w-5',
                              isSelected ? 'text-primary' : 'text-muted-foreground'
                            )}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-foreground truncate">{loc.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {getStorageTypeLabel(loc.type)} ·{' '}
                            <Plural value={loc.itemCount ?? 0} one="# item" other="# items" />
                          </p>
                        </div>
                        {isSelected && (
                          <CheckCircleIcon className="h-5 w-5 text-primary flex-shrink-0" />
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {error && (
              <div className="rounded-xl bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive animate-in fade-in slide-in-from-top-2">
                {error}
              </div>
            )}

            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setStep('confirm')}
                className="rounded-xl"
              >
                <Trans>Back</Trans>
              </Button>
              <Button
                onClick={handleMoveItems}
                disabled={!selectedDestination || otherLocations.length === 0}
                className="rounded-xl"
              >
                <CheckCircleIcon className="h-4 w-4 mr-2" />
                <Trans>Move Items</Trans>
              </Button>
            </DialogFooter>
          </>
        );

      case 'moving':
        return (
          <div className="py-8 flex flex-col items-center gap-4">
            <div className="h-10 w-10 animate-spin rounded-full border-3 border-primary border-t-transparent" />
            <div className="text-center">
              <p className="font-medium text-foreground">
                <Trans>Moving items...</Trans>
              </p>
              <p className="text-sm text-muted-foreground mt-1">
                <Trans>
                  Moving <Plural value={localItemCount} one="# item" other="# items" /> to the
                  selected location
                </Trans>
              </p>
            </div>
          </div>
        );

      case 'deleting':
        return (
          <div className="py-8 flex flex-col items-center gap-4">
            <div className="h-10 w-10 animate-spin rounded-full border-3 border-destructive border-t-transparent" />
            <p className="font-medium text-foreground">
              <Trans>Deleting location...</Trans>
            </p>
          </div>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={step !== 'moving' && step !== 'deleting' ? handleClose : undefined}
    >
      <DialogContent className="sm:max-w-[425px]">{renderContent()}</DialogContent>
    </Dialog>
  );
}
